/*
 * This component can handle any of our types of images.
 *
 * <Image image={blok.asset} /> // storyblock asset
 * <Image image={blok.image} /> // storyblock legacy image; just has image as string url
 * <Image image={`base64-encoded data`} />
 * <Image image={url} /> // anything else
 */
import { ReactNode } from 'react'

import { StaticImageData } from 'next/image'

import initTracer from '@/lib/utils/tracer'

import LocalImage from './local-image'
import OptimizedImage from './optimized-image'
import SbAssetImage from './sb-asset-image'
import SbLegacyImage from './sb-legacy-image'
import SupabaseImage from './supabase-image'
import type { ImageProps, StoryblokAsset, SupabaseImageProps } from './types'

const Image = (props: ImageProps) => {
  const { image, origin, tracer = initTracer() } = props
  tracer.trace('Image (%s): %o', origin, props)
  try {
    let imageType = null
    let img: ReactNode | null = null
    if ((image as SupabaseImageProps)?.source === 'supabase') {
      imageType = 'supabase'
      tracer.trace('found a supabase image')
      img = SupabaseImage({ ...props, tracer })
    } else if (typeof image === 'string' && /storage\/v1\/object\/public\/assets\//.test(image as string)) {
      imageType = 'supabase'
      tracer.trace('found a supabase image')
      img = SupabaseImage({ ...props, tracer })
    } else if (typeof image === 'string' && /storyblok/.test(image as string)) {
      imageType = 'legacy'
      tracer.trace('found a storyblok legacy image')
      img = SbLegacyImage({ ...props, tracer })
    } else if ((image as StoryblokAsset)?.filename) {
      imageType = 'asset'
      tracer.trace('found a storyblok asset image')
      img = SbAssetImage({ ...props, tracer })
    } else if (typeof image === 'string' && /http/.test(image as string)) {
      imageType = 'external'
      tracer.trace('found an external image')
      img = OptimizedImage({ ...props, tracer })
    } else if (typeof image === 'string' && /data.*base64/.test(image as string)) {
      imageType = 'base64'
      tracer.trace('found an inline image')
      img = OptimizedImage({ ...props, tracer })
    } else if (typeof image === 'string') {
      imageType = 'local'
      tracer.trace('found a local image')
      // LocalImage is a client component: calling it as a function from the
      // server throws — render it as JSX, and keep the tracer (an object of
      // functions) out of the serialized client props.
      const { tracer: _tracer, ...clientProps } = props
      img = <LocalImage {...clientProps} />
    } else if (image && (image as StaticImageData).src) {
      imageType = 'static'
      tracer.trace('found a static image')
      const { src } = image as StaticImageData
      img = OptimizedImage({ ...props, tracer, image: src })
    } else if (typeof image === 'function') {
      imageType = 'react'
      tracer.trace('found a component image')
      // This works for svgs, but doesn't allow for any optimization so not very useful
      const MustBeSvg = image
      if (props.sizingProps?.fixed) {
        const { fixedWidth, fixedHeight } = props.sizingProps
        return (
          <div style={{ width: fixedWidth, height: fixedHeight }}>
            <MustBeSvg data-origin={origin} {...props.imgProps} />
          </div>
        )
      }
      return <MustBeSvg data-origin={origin} />
    } else {
      imageType = 'unknown'
      tracer.trace('Could not find matching image type')
    }
    if (img === null) {
      // TODO: maybe return a generic dummy image
      tracer.trace('ERROR: null image: %o', image)
    }
    tracer.trace('image type: %s', imageType)
    return img
  } catch (ex: unknown) {
    if ((ex as Error).message !== undefined) {
      tracer.trace('Exception in image: %s', (ex as Error).message)
    }
    if (process.env.NODE_ENV !== 'production') {
      throw ex
    }
  } finally {
    tracer.log(origin)
  }
  return null
}

export default Image
